import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export interface PromptRunRequest {
  message: string;
  language: string;
  session_key: string;
  maxToken: number;
  agent_id?: string;
  async?: boolean;
}

export interface PromptRunResponse {
  status: string;
  output?:
    | {
        text: string;
      }
    | string;
  output_payload?: {
    text: string;
  };
  steps?: {
    step: number;
    raw?: string;
    parsed?: {
      type: string;
      text: string;
    };
    provider?: string;
    model?: string;
  }[];
  error?: string | null;
  provider?: string;
  model?: string;
  session_key?: string;
  max_tokens?: number;
  tokens_used?: number;
  tokens_balance?: number;
  tokensBalance?: number;
  language?: string;
  job_id?: string | null;
  jobId?: string | null;
  async_run?: boolean;
  status_url?: string | null;
  result_url?: string | null;
  done?: boolean;
}

// Resolve agent config based on VITE_AI_PROFILE
import {
  getPromptAgentConfig,
  resolveAgentId,
} from "@src/config/promptAgentConfig";

const agentConfig = getPromptAgentConfig();

export const PromptApiSlice = createApi({
  reducerPath: "promptApi",
  baseQuery: fetchBaseQuery({
    baseUrl: agentConfig.baseUrl,
  }),
  endpoints: (builder) => ({
    sendPromptMessage: builder.mutation<PromptRunResponse, PromptRunRequest>({
      query: (payload) => {
        const { agent_id, ...bodyData } = payload;
        const targetAgentId =
          agent_id || resolveAgentId(payload.language);
        let url = agentConfig.urlPath;
        if (url.includes("agent_id=")) {
          url = url.replace(/agent_id=[^&]+/, `agent_id=${targetAgentId}`);
        } else {
          url = `${url}${url.includes("?") ? "&" : "?"}agent_id=${targetAgentId}`;
        }

        return {
          url,
          method: "POST",
          body: bodyData,
          headers: agentConfig.headers,
        };
      },
    }),
    getPromptJobResult: builder.mutation<
      PromptRunResponse,
      { jobId: string; customUrl?: string }
    >({
      query: ({ jobId, customUrl }) => ({
        url: customUrl || `${agentConfig.jobResultPath}/${jobId}`,
        method: "GET",
        headers: agentConfig.headers,
      }),
    }),
    checkPromptJobStatus: builder.mutation<
      PromptRunResponse,
      { jobId: string; customUrl?: string }
    >({
      query: ({ jobId, customUrl }) => ({
        url: customUrl || `${agentConfig.jobStatusPath}/${jobId}/status`,
        method: "GET",
        headers: agentConfig.headers,
      }),
    }),
  }),
});

export const {
  useSendPromptMessageMutation,
  useGetPromptJobResultMutation,
  useCheckPromptJobStatusMutation,
} = PromptApiSlice;

